import React, { useState, useCallback, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, Platform, ScrollView, KeyboardAvoidingView } from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api } from '../../src/api/client';
import { useAuth } from '../../src/context/AuthContext';
import { useActiveSession } from '../../src/context/ActiveSessionContext';
import { useToast } from '../../src/components/Toast';
import { useTheme } from '../../src/context/ThemeContext';
import type { ColorTheme } from '../../src/utils/theme';
import Button from '../../src/components/Button';
import Card from '../../src/components/Card';
import ContentContainer from '../../src/components/ContentContainer';
import QRScannerModal from '../../src/components/QRScannerModal';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import { useResponsive, rs } from '../../src/utils/responsive';

/** UserScanScreen - QR code scanner and manual URL entry for starting a wash session. */
export default function UserScanScreen() {
  const [manualUrl, setManualUrl] = useState('');
  // Active session comes from the ONE shared ActiveSessionContext poller
  // (also feeds the global ReturnBanner) — this screen keeps its richer card
  // but runs no poll loop of its own.
  const { session: activeSession, refresh: refreshActiveSession } = useActiveSession();
  const [scannerOpen, setScannerOpen] = useState(false);
  const [hasPaymentMethods, setHasPaymentMethods] = useState(true);
  const [isFleetMember, setIsFleetMember] = useState(false);
  const router = useRouter();
  const toast = useToast();
  const { role } = useAuth();
  const { colors } = useTheme();
  const { isDesktopWeb } = useResponsive();
  const styles = useStyles(colors);

  const checkPaymentMethods = async () => {
    if (role === 'admin') return;
    try {
      const res = await api<{ hasPaymentMethods: boolean }>('/api/payments/has-methods');
      setHasPaymentMethods(res.hasPaymentMethods);
    } catch { /* non-fatal */ }
  };

  const checkFleetMembership = async () => {
    try {
      const res = await api<any[]>('/api/fleet/me/memberships');
      setIsFleetMember(res.some((m: any) => m.status === 'active'));
    } catch { /* non-fatal */ }
  };

  // Preset banner countdown
  const [bannerRemaining, setBannerRemaining] = useState<number | null>(null);
  const bannerTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (bannerTimerRef.current) clearInterval(bannerTimerRef.current);
    if (activeSession?.active && activeSession.isPreset && activeSession.presetEndsAt) {
      const tick = () => {
        const rem = Math.max(0, Math.ceil((new Date(activeSession.presetEndsAt!).getTime() - Date.now()) / 1000));
        setBannerRemaining(rem);
      };
      tick();
      bannerTimerRef.current = setInterval(tick, 1000);
      return () => { if (bannerTimerRef.current) clearInterval(bannerTimerRef.current); };
    } else {
      setBannerRemaining(null);
    }
  }, [activeSession?.sessionId, activeSession?.isPreset, activeSession?.presetEndsAt]);

  useFocusEffect(useCallback(() => {
    // Nudge the shared poller so the card is fresh the instant we land here;
    // the context's own 5s interval keeps it updated after that.
    refreshActiveSession();
    checkPaymentMethods();
    checkFleetMembership();
  }, []));

  /**
   * Route a pasted value: a wash URL/path → the wash screen; a vending URL →
   * the vend screen; a redeem link or a bare promo/gift code → the redemption
   * screen (which claims it to the account). Codes are the 10-char voucher
   * alphabet but we accept any short alphanumeric string and let the redeem
   * screen validate.
   */
  const handleManualEntry = () => {
    const raw = manualUrl.trim();
    const washMatch = raw.match(/wash\/([^/\s?#]+)\/([^/\s?#]+)/);
    if (washMatch) {
      router.push(`/wash/${washMatch[1]}/${washMatch[2]}` as never);
      return;
    }
    const vendMatch = raw.match(/vend\/([^/\s?#]+)\/([^/\s?#]+)/i);
    if (vendMatch) {
      router.push(`/vend/${vendMatch[1]}/${vendMatch[2]}` as never);
      return;
    }
    const redeemMatch = raw.match(/redeem\/([^/\s?#]+)/i);
    if (redeemMatch) {
      router.push(`/redeem/${redeemMatch[1].toUpperCase()}` as never);
      return;
    }
    if (/^[A-Za-z0-9]{4,20}$/.test(raw)) {
      router.push(`/redeem/${raw.toUpperCase()}` as never);
      return;
    }
    toast.show({ type: 'error', title: 'Not recognized', message: 'Enter a wash URL or a promo/gift code' });
  };

  /** Extract a wash / vending / redeem URL from scanned QR data and navigate. */
  const handleQRScanned = (data: string) => {
    setScannerOpen(false);
    const washMatch = data.match(/wash\/([^/\s?#]+)\/([^/\s?#]+)/);
    if (washMatch) {
      router.push(`/wash/${washMatch[1]}/${washMatch[2]}` as never);
      return;
    }
    // Vending QRs deep-link /vend/{locationId}/{machineId} — same scanner.
    const vendMatch = data.match(/vend\/([^/\s?#]+)\/([^/\s?#]+)/i);
    if (vendMatch) {
      router.push(`/vend/${vendMatch[1]}/${vendMatch[2]}` as never);
      return;
    }
    // Gift/promo QRs (parity with manual entry).
    const redeemMatch = data.match(/redeem\/([^/\s?#]+)/i);
    if (redeemMatch) {
      router.push(`/redeem/${redeemMatch[1].toUpperCase()}` as never);
      return;
    }
    toast.show({ type: 'error', title: 'Invalid QR Code', message: 'This QR code is not a valid RinsePay code' });
  };

  const openScanner = () => {
    if (Platform.OS === 'web' && isDesktopWeb) {
      toast.show({ type: 'info', title: 'Camera', message: 'QR scanning is available on the mobile app. Use the manual entry below on web.' });
    } else {
      setScannerOpen(true);
    }
  };

  return (
    <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
      <ContentContainer>
      {/* Active session banner */}
      {activeSession?.active && (
        <TouchableOpacity onPress={() => {
          if (activeSession.locationId && activeSession.bayId) {
            router.push(`/wash/${activeSession.locationId}/${activeSession.bayId}` as never);
          }
        }}>
          <Card style={styles.activeCard}>
            <View style={styles.activeRow}>
              <View style={styles.activePulse} />
              <View style={styles.activeInfo}>
                <Text style={styles.activeTitle}>
                  {activeSession.bayType === 'vacuum' ? 'Vacuum in Progress' : 'Wash in Progress'}
                </Text>
                <Text style={styles.activeSubtext}>{activeSession.locationName} — {activeSession.bayName}</Text>
              </View>
              {activeSession.isPreset && bannerRemaining != null ? (
                <Text style={styles.activeCost}>{Math.floor(bannerRemaining / 60)}:{(bannerRemaining % 60).toString().padStart(2, '0')}</Text>
              ) : (
                <Text style={styles.activeCost}>${((activeSession.currentCostCents || 0) / 100).toFixed(2)}</Text>
              )}
            </View>
          </Card>
        </TouchableOpacity>
      )}

      {!hasPaymentMethods && role !== 'admin' && !isFleetMember && (
        <TouchableOpacity
          style={styles.paymentPrompt}
          onPress={() => router.push('/payment-methods?returnTo=wash')}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Add a payment method"
        >
          <Ionicons name="card-outline" size={22} color="#92400e" />
          <View style={{ flex: 1, marginLeft: spacing.sm }}>
            <Text style={styles.paymentPromptTitle}>Payment Method Required</Text>
            <Text style={styles.paymentPromptText}>Tap to add a card</Text>
          </View>
          <Ionicons name="chevron-forward" size={20} color="#92400e" />
        </TouchableOpacity>
      )}

      {!hasPaymentMethods && isFleetMember && (
        <TouchableOpacity
          style={styles.fleetInfoPrompt}
          onPress={() => router.push('/payment-methods?returnTo=wash')}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Add a personal payment method"
        >
          <Ionicons name="business-outline" size={22} color="#1e40af" />
          <View style={{ flex: 1, marginLeft: spacing.sm }}>
            <Text style={styles.fleetInfoTitle}>No personal card on file</Text>
            <Text style={styles.fleetInfoText}>Fleet washes still available — tap to add a personal card</Text>
          </View>
          <Ionicons name="chevron-forward" size={20} color="#1e40af" />
        </TouchableOpacity>
      )}

      <View style={styles.hero}>
        {!(Platform.OS === 'web' && isDesktopWeb) ? (
          <TouchableOpacity style={styles.scanIconContainer} onPress={openScanner} activeOpacity={0.7}>
            <Ionicons name="scan" size={80} color={colors.primary} />
          </TouchableOpacity>
        ) : (
          <View style={styles.scanIconContainer}>
            <Ionicons name="scan" size={80} color={colors.primary} />
          </View>
        )}
        <Text style={styles.title}>Scan to Wash</Text>
        <Text style={styles.subtitle}>Point your camera at the QR code on the car wash bay</Text>
      </View>

      {Platform.OS === 'web' && isDesktopWeb ? (
        <View style={styles.webCameraNote}>
          <Ionicons name="phone-portrait-outline" size={18} color="#dc2626" />
          <Text style={styles.webCameraText}>Camera scanning available on mobile app</Text>
        </View>
      ) : (
        <>
          <TouchableOpacity style={styles.scanButton} onPress={openScanner}>
            <Ionicons name="camera" size={24} color={colors.white} />
            <Text style={styles.scanButtonText}>Open Scanner</Text>
          </TouchableOpacity>
          <QRScannerModal
            visible={scannerOpen}
            onClose={() => setScannerOpen(false)}
            onScanned={handleQRScanned}
          />
        </>
      )}

      <View style={styles.divider}>
        <View style={styles.line} />
        <Text style={styles.dividerText}>or enter URL manually</Text>
        <View style={styles.line} />
      </View>

      <View style={styles.manualEntry}>
        <TextInput
          style={styles.input}
          placeholder="Paste wash URL or code"
          placeholderTextColor={colors.textLight}
          value={manualUrl}
          onChangeText={setManualUrl}
          autoCapitalize="none"
          onSubmitEditing={handleManualEntry}
          returnKeyType="go"
          maxLength={255}
        />
        <TouchableOpacity style={styles.goBtn} onPress={handleManualEntry}>
          <Ionicons name="arrow-forward" size={20} color={colors.white} />
        </TouchableOpacity>
      </View>

      <Button
        title="Add or update payments"
        variant="outline"
        onPress={() => router.push('/payment-methods?returnTo=wash')}
        style={{ marginTop: spacing.xl }}
        icon={<Ionicons name="card-outline" size={18} color={colors.primary} />}
      />
      </ContentContainer>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  scroll: { flexGrow: 1, padding: spacing.lg, paddingBottom: 200 },

  activeCard: { marginBottom: spacing.md, backgroundColor: c.success + '08', borderWidth: 1, borderColor: c.success + '30' },
  activeRow: { flexDirection: 'row', alignItems: 'center' },
  activePulse: { width: 12, height: 12, borderRadius: 6, backgroundColor: c.success, marginRight: spacing.md },
  activeInfo: { flex: 1 },
  activeTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.success },
  activeSubtext: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 1 },
  activeCost: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.success },

  hero: { alignItems: 'center', marginTop: spacing.lg, marginBottom: spacing.xl },
  scanIconContainer: {
    width: rs(140), height: rs(140), borderRadius: borderRadius.xl,
    backgroundColor: c.primaryBg,
    justifyContent: 'center', alignItems: 'center',
    marginBottom: spacing.lg,
    borderWidth: 3, borderColor: c.primary + '30',
    borderStyle: 'dashed',
  },
  title: { fontSize: fontSize.xxl, fontWeight: fontWeight.bold, color: c.text },
  subtitle: { fontSize: fontSize.md, color: c.textSecondary, textAlign: 'center', marginTop: spacing.xs, maxWidth: rs(280) },
  scanButton: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: c.primary, borderRadius: borderRadius.md,
    paddingVertical: spacing.md, gap: spacing.sm,
    shadowColor: c.primary, shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2, shadowRadius: 8, elevation: 4,
  },
  scanButtonText: { fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: c.white },
  webCameraNote: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs,
    paddingVertical: spacing.md,
  },
  webCameraText: { fontSize: fontSize.sm, color: '#dc2626' },
  divider: { flexDirection: 'row', alignItems: 'center', marginVertical: spacing.lg },
  line: { flex: 1, height: 1, backgroundColor: c.border },
  dividerText: { marginHorizontal: spacing.md, fontSize: fontSize.sm, color: c.textLight },
  manualEntry: { flexDirection: 'row', gap: spacing.sm },
  input: {
    flex: 1, backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.md,
    paddingHorizontal: spacing.md, paddingVertical: spacing.md - 2,
    fontSize: fontSize.md, color: c.text,
    borderWidth: 1, borderColor: c.border,
  },
  goBtn: {
    width: rs(48), borderRadius: borderRadius.md,
    backgroundColor: c.primary, justifyContent: 'center', alignItems: 'center',
  },

  paymentPrompt: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#fffbeb', borderWidth: 1, borderColor: '#fbbf24',
    borderRadius: borderRadius.lg, padding: spacing.md, marginBottom: spacing.md,
  },
  paymentPromptTitle: {
    fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: '#92400e',
  },
  paymentPromptText: {
    fontSize: fontSize.xs, color: '#a16207', marginTop: 2,
  },

  fleetInfoPrompt: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#eff6ff', borderWidth: 1, borderColor: '#93c5fd',
    borderRadius: borderRadius.lg, padding: spacing.md, marginBottom: spacing.md,
  },
  fleetInfoTitle: {
    fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: '#1e40af',
  },
  fleetInfoText: {
    fontSize: fontSize.xs, color: '#1d4ed8', marginTop: 2,
  },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
